/**
 * Catalog of Uruguayan financial institutions eligible for receiving payouts.
 *
 * This file is the single source of truth consumed by:
 *   - Backend validation (apps/backend)
 *   - Portal form (apps/portal)
 *   - The professional app's bank form, which imports this package directly
 *
 * THE DART MIRROR IS GONE, and with it the codegen that kept it in sync. The
 * Flutter professional app held a generated `uruguay_banks.g.dart` because
 * Dart could not import a TypeScript package; its React Native replacement
 * imports this file, so there is one copy again and nothing to keep in sync.
 * Editing this list no longer needs a regeneration step.
 */
/**
 * Source: BCU "Instituciones Habilitadas a Operar en el Sistema de Pagos Moneda Local" PDF,
 * cross-referenced with each bank's published SPI receiver format (BROU, Scotia, Santander, Itaú).
 *
 * EDEs / cooperatives without a published 3-digit BCU code use acquirer TEF codes; these are
 * marked `bcuCodeConfirmed: false` and should be verified with the acquiring partner before go-live.
 */
export const URUGUAY_BANKS = [
    // --- Banks (BCU codes confirmed) ---
    {
        code: '001',
        shortName: 'BROU',
        officialName: 'Banco de la República Oriental del Uruguay',
        category: 'bank',
        accountRegex: '^\\d{12}$',
        canonicalLength: 12,
        padLeft: true,
        accountTypeApplies: true,
        accountFormatHint: '12 dígitos — SSS-PP-NNNNNN-V',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '091',
        shortName: 'BHU',
        officialName: 'Banco Hipotecario del Uruguay',
        category: 'bank',
        accountRegex: '^\\d{12}$',
        canonicalLength: 12,
        padLeft: true,
        accountTypeApplies: true,
        accountFormatHint: '12 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '110',
        shortName: 'Bandes',
        officialName: 'Banco Bandes Uruguay S.A.',
        category: 'bank',
        accountRegex: '^\\d{9}$',
        canonicalLength: 9,
        padLeft: true,
        accountTypeApplies: true,
        accountFormatHint: '9 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '113',
        shortName: 'Itaú',
        officialName: 'Banco Itaú Uruguay S.A.',
        category: 'bank',
        accountRegex: '^\\d{7}$',
        canonicalLength: 7,
        padLeft: true,
        accountTypeApplies: true,
        accountFormatHint: '7 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '128',
        shortName: 'Scotiabank',
        officialName: 'Scotiabank Uruguay S.A.',
        category: 'bank',
        accountRegex: '^\\d{10}$',
        canonicalLength: 10,
        padLeft: true,
        accountTypeApplies: true,
        accountFormatHint: '10 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '137',
        shortName: 'Santander',
        officialName: 'Banco Santander S.A.',
        category: 'bank',
        accountRegex: '^\\d{16}$',
        canonicalLength: 16,
        padLeft: true,
        accountTypeApplies: true,
        accountFormatHint: '16 dígitos — 4 sucursal + 12 cuenta',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '153',
        shortName: 'BBVA',
        officialName: 'Banco Bilbao Vizcaya Argentaria Uruguay S.A.',
        category: 'bank',
        accountRegex: '^\\d{1,9}$',
        padLeft: false,
        accountTypeApplies: true,
        accountFormatHint: 'Hasta 9 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '157',
        shortName: 'HSBC',
        officialName: 'HSBC Bank (Uruguay) S.A.',
        category: 'bank',
        accountRegex: '^\\d{4,10}$',
        padLeft: false,
        accountTypeApplies: true,
        accountFormatHint: '4 a 10 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '162',
        shortName: 'Heritage',
        officialName: 'Banque Heritage (Uruguay) S.A.',
        category: 'bank',
        accountRegex: '^\\d{9}$',
        canonicalLength: 9,
        padLeft: true,
        accountTypeApplies: true,
        accountFormatHint: '9 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '205',
        shortName: 'Citibank',
        officialName: 'Citibank N.A. Sucursal Uruguay',
        category: 'bank',
        accountRegex: '^[015]\\d{9}$',
        canonicalLength: 10,
        padLeft: false,
        accountTypeApplies: true,
        accountFormatHint: '10 dígitos (inicia con 0, 1 o 5)',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '246',
        shortName: 'Banco Nación',
        officialName: 'Banco de la Nación Argentina (Uruguay)',
        category: 'bank',
        accountRegex: '^\\d{1,12}$',
        padLeft: false,
        accountTypeApplies: true,
        accountFormatHint: 'Hasta 12 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '361',
        shortName: 'BAPRO',
        officialName: 'Banco de la Provincia de Buenos Aires (Uruguay)',
        category: 'bank',
        accountRegex: '^\\d{1,7}$',
        padLeft: false,
        accountTypeApplies: true,
        accountFormatHint: 'Hasta 7 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    // --- Casas financieras (BCU codes confirmed) ---
    {
        code: '452',
        shortName: 'Fortex',
        officialName: 'Fortex Servicios Financieros S.A.',
        category: 'casa_financiera',
        accountRegex: '^\\d{1,8}$',
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: 'Hasta 8 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '466',
        shortName: 'Aspen',
        officialName: 'Aspen Servicios Financieros S.A.',
        category: 'casa_financiera',
        accountRegex: '^\\d{1,9}$',
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: 'Hasta 9 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '556',
        shortName: 'VAL',
        officialName: 'VAL Servicios Financieros S.A.',
        category: 'casa_financiera',
        accountRegex: '^\\d{1,8}$',
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: 'Hasta 8 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '558',
        shortName: 'Indumex',
        officialName: 'Indumex Servicios Financieros S.A.',
        category: 'casa_financiera',
        accountRegex: '^\\d{1,8}$',
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: 'Hasta 8 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '564',
        shortName: 'Delta',
        officialName: 'Centro Sudamericano Servicios Financieros (Delta)',
        category: 'casa_financiera',
        accountRegex: '^\\d{1,8}$',
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: 'Hasta 8 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '566',
        shortName: 'Gales',
        officialName: 'Gales Servicios Financieros S.A.',
        category: 'casa_financiera',
        accountRegex: '^\\d{1,13}$',
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: 'Hasta 13 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '575',
        shortName: 'Multidivisa',
        officialName: 'Multidivisa S.A.',
        category: 'casa_financiera',
        accountRegex: '^\\d{1,10}$',
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: 'Hasta 10 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    {
        code: '587',
        shortName: 'Nixus',
        officialName: 'Nixus S.A. Servicios Financieros',
        category: 'casa_financiera',
        accountRegex: '^\\d{1,8}$',
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: 'Hasta 8 dígitos',
        bcuCodeConfirmed: true,
        active: true,
    },
    // --- Cooperativas (acquirer TEF codes — verify with SPI provider before go-live) ---
    {
        code: '860',
        shortName: 'ANDA',
        officialName: 'Asociación Nacional de Afiliados',
        category: 'cooperativa',
        accountRegex: '^\\d{5}$',
        canonicalLength: 5,
        padLeft: true,
        accountTypeApplies: false,
        accountFormatHint: '5 dígitos',
        bcuCodeConfirmed: false,
        active: true,
    },
    {
        code: '861',
        shortName: 'FUCAC',
        officialName: 'FUCAC — Cooperativa de Ahorro y Crédito',
        category: 'cooperativa',
        accountRegex: '^\\d{14}$',
        canonicalLength: 14,
        padLeft: true,
        accountTypeApplies: false,
        accountFormatHint: '14 dígitos',
        bcuCodeConfirmed: false,
        active: true,
    },
    {
        code: '862',
        shortName: 'FUCEREP',
        officialName: 'FUCEREP — Cooperativa de Ahorro y Crédito',
        category: 'cooperativa',
        accountRegex: '^\\d{1,9}$',
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: 'Hasta 9 dígitos',
        bcuCodeConfirmed: false,
        active: true,
    },
    // --- EDEs / wallets (acquirer TEF codes — verify with SPI provider before go-live) ---
    {
        code: '870',
        shortName: 'Mercado Pago',
        officialName: 'Mercado Pago Uruguay S.A.',
        category: 'ede',
        accountRegex: '^\\d{13}$',
        canonicalLength: 13,
        padLeft: true,
        accountTypeApplies: false,
        accountFormatHint: '13 dígitos',
        bcuCodeConfirmed: false,
        active: true,
    },
    {
        code: '871',
        shortName: 'Prex',
        officialName: 'Prex Uruguay S.A.',
        category: 'ede',
        accountRegex: '^\\d{1,8}$',
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: 'Hasta 8 dígitos',
        bcuCodeConfirmed: false,
        active: true,
    },
    {
        code: '872',
        shortName: 'OCA Blue',
        officialName: 'OCA Blue — OCA S.A.',
        category: 'ede',
        accountRegex: '^\\d{7}$',
        canonicalLength: 7,
        padLeft: true,
        accountTypeApplies: false,
        accountFormatHint: '7 dígitos',
        bcuCodeConfirmed: false,
        active: true,
    },
    {
        code: '873',
        shortName: 'MiDinero',
        officialName: 'MiDinero S.A.',
        category: 'ede',
        accountRegex: '^\\d{3,11}$',
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: '3 a 11 dígitos',
        bcuCodeConfirmed: false,
        active: true,
    },
    {
        code: '874',
        shortName: 'Nobilis',
        officialName: 'Nobilis S.A.',
        category: 'ede',
        accountRegex: '^\\d{6}$',
        canonicalLength: 6,
        padLeft: true,
        accountTypeApplies: false,
        accountFormatHint: '6 dígitos',
        bcuCodeConfirmed: false,
        active: true,
    },
    {
        code: '875',
        shortName: 'Avenida',
        officialName: 'Avenida Servicios Financieros S.A.',
        category: 'ede',
        accountRegex: '^\\d{1,8}$',
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: 'Hasta 8 dígitos',
        bcuCodeConfirmed: false,
        active: true,
    },
    {
        code: '876',
        shortName: 'Grin',
        officialName: 'Grin S.A.',
        category: 'ede',
        accountRegex: '^404\\d{10}$',
        canonicalLength: 13,
        padLeft: false,
        accountTypeApplies: false,
        accountFormatHint: '13 dígitos que inician con 404',
        bcuCodeConfirmed: false,
        active: true,
    },
];
const BANK_BY_CODE = URUGUAY_BANKS.reduce((acc, b) => {
    acc[b.code] = b;
    return acc;
}, {});
export function getUruguayBank(code) {
    return BANK_BY_CODE[code.trim()] ?? null;
}
/**
 * Strip user-entered separators from an account number.
 * Does NOT pad — use `applyAccountNumberPadding` for that.
 */
export function stripAccountNumberSeparators(raw) {
    return raw.replace(/[\s\-\.]/g, '').trim();
}
/**
 * Left-pad to the bank's canonical length with '0' when `padLeft` is true.
 * Returns the input unchanged if the bank has no canonical length or padding is disabled.
 */
export function applyAccountNumberPadding(bank, value) {
    if (!bank.padLeft || bank.canonicalLength == null)
        return value;
    if (value.length >= bank.canonicalLength)
        return value;
    return value.padStart(bank.canonicalLength, '0');
}
/** Full normalization: strip separators, then apply padding. */
export function normalizeAccountNumber(bank, raw) {
    return applyAccountNumberPadding(bank, stripAccountNumberSeparators(raw));
}
export function validateAccountNumber(bankCode, rawAccountNumber) {
    const bank = getUruguayBank(bankCode);
    if (!bank)
        return { ok: false, reason: 'unknown_bank' };
    const stripped = stripAccountNumberSeparators(rawAccountNumber);
    if (stripped.length === 0)
        return { ok: false, reason: 'empty' };
    const padded = applyAccountNumberPadding(bank, stripped);
    if (!new RegExp(bank.accountRegex).test(padded)) {
        return { ok: false, reason: 'format' };
    }
    return { ok: true, normalized: padded };
}
/**
 * Accept Uruguayan CI (6–8 digits + optional 1-digit check) or RUT (12 digits).
 * Strips dots, dashes, spaces before matching.
 */
export function validateHolderDocument(raw) {
    const cleaned = raw.replace(/[.\-\s]/g, '').trim();
    if (cleaned.length === 0)
        return { ok: false, reason: 'empty' };
    if (/^\d{12}$/.test(cleaned))
        return { ok: true, kind: 'rut', normalized: cleaned };
    if (/^\d{6,8}\d?$/.test(cleaned))
        return { ok: true, kind: 'ci', normalized: cleaned };
    return { ok: false, reason: 'format' };
}
export const ACCOUNT_TYPES = ['savings', 'checking'];
export function accountTypeLabelEs(t) {
    return t === 'savings' ? 'Caja de ahorro' : 'Cuenta corriente';
}
